"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import { extractProperty, getUmbracoMediaUrl } from "utils"

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding-block: 40px;
`

const StyledTitle = styled.h2`
  margin: 0;
  font-size: 28px;
  font-weight: 400;
  line-height: 1.25;
  letter-spacing: -0.8px;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
  text-transform: capitalize;
  width: 100%;

  span {
    color: #0000fa;
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 40px;
  }
`

const StyledGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 40px;
  }
`

const StyledItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  width: calc(50% - 12px);

  ${({ theme }) => theme.breakpoints.up("md")} {
    width: calc(50% - 20px);
  }
`

const StyledIconWrap = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
`

const StyledItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledItemTitle = styled.p`
  margin: 0;
  font-size: 20px;
  font-weight: 300;
  line-height: 1.5;
  text-transform: capitalize;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};

  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

const StyledItemSubtitle = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 300;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};

  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

type ServicesGridProps = {
  blackTitle: string
  blueTitle: string
  items: any[]
}

export default function ServicesGrid({ blackTitle, blueTitle, items }: ServicesGridProps) {
  if (!items || items.length === 0) return null

  return (
    <StyledSection>
      <StyledTitle>
        {blackTitle} <span>{blueTitle}</span>
      </StyledTitle>
      <StyledGrid>
        {items.map((item: any, index: number) => {
          const icon = extractProperty(item, "icon")
          const title = extractProperty(item, "title") || ""
          const subtitle = extractProperty(item, "subtitle") || ""
          return (
            <StyledItem key={index}>
              {icon && (
                <StyledIconWrap>
                  <Image
                    src={getUmbracoMediaUrl(icon)}
                    width={50}
                    height={50}
                    alt={title || "service icon"}
                  />
                </StyledIconWrap>
              )}
              <StyledItemInfo>
                {title && <StyledItemTitle>{title}</StyledItemTitle>}
                {subtitle && <StyledItemSubtitle>{subtitle}</StyledItemSubtitle>}
              </StyledItemInfo>
            </StyledItem>
          )
        })}
      </StyledGrid>
    </StyledSection>
  )
}
